import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../brand/BrandLogo';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import ThemeToggle from '../ui/ThemeToggle';
import { useAuth } from '../../context/AuthContext';
import HeaderSignOutButton from '../layout/HeaderSignOutButton';
import ClientSessionLayout from '../layout/ClientSessionLayout';

const ExploreAuthLinks = ({ onNavigate, stacked }) => {
  const { t } = useTranslation();
  const { isAuthenticated, role } = useAuth();

  const linkClass = stacked
    ? 'rounded-lg px-3 py-2.5 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-900'
    : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white';
  const ctaClass = stacked
    ? 'rounded-lg bg-glow-600 px-3 py-2.5 text-center text-sm text-white'
    : 'rounded-lg bg-glow-600 px-3 py-1.5 text-white';

  if (isAuthenticated) {
    const dash =
      role === 'CLIENT'
        ? '/client/dashboard'
        : role === 'SALON_MANAGER'
          ? '/salon/dashboard'
          : '/login';
    return (
      <>
        <Link to={dash} className={ctaClass} onClick={onNavigate}>
          {t('common.dashboard')}
        </Link>
        <HeaderSignOutButton
          className={
            stacked
              ? 'w-full rounded-lg px-3 py-2.5 text-left text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-900'
              : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'
          }
        />
      </>
    );
  }

  return (
    <>
      <Link to="/login" className={linkClass} onClick={onNavigate}>
        {t('common.signIn')}
      </Link>
      <Link to="/register" className={ctaClass} onClick={onNavigate}>
        {t('common.join')}
      </Link>
    </>
  );
};

const ExplorePublicLayout = ({ children }) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen min-w-0 overflow-x-hidden bg-zinc-50 dark:bg-zinc-950">
      <header className="sticky top-0 z-30 border-b border-zinc-200 bg-zinc-50/90 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/90">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <BrandLogo to="/explore" size="sm" showWordmark onClick={closeMenu} />

          <nav className="hidden items-center gap-4 text-sm md:flex">
            <LanguageSwitcher />
            <ThemeToggle />
            <Link
              to="/marketplace"
              className="text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
            >
              {t('common.marketplace')}
            </Link>
            <ExploreAuthLinks />
          </nav>

          <button
            type="button"
            className="rounded-lg p-2 text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800 md:hidden"
            aria-label={menuOpen ? t('common.closeMenu') : t('common.openMenu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>

        {menuOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-black/40 md:hidden"
              aria-label={t('common.closeMenu')}
              onClick={closeMenu}
            />
            <div className="absolute inset-x-0 top-full z-50 border-b border-zinc-200 bg-zinc-50 p-4 shadow-lg dark:border-zinc-800 dark:bg-zinc-950 md:hidden">
              <nav className="flex flex-col gap-2">
                <LanguageSwitcher className="w-full" selectClassName="w-full" />
                <ThemeToggle className="w-full justify-center" />
                <Link
                  to="/marketplace"
                  onClick={closeMenu}
                  className="rounded-lg px-3 py-2.5 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-900"
                >
                  {t('common.marketplace')}
                </Link>
                <ExploreAuthLinks onNavigate={closeMenu} stacked />
              </nav>
            </div>
          </>
        )}
      </header>
      {children}
    </div>
  );
};

const ExploreLayout = ({ children }) => (
  <ClientSessionLayout publicLayout={<ExplorePublicLayout>{children}</ExplorePublicLayout>}>
    <div className="min-h-full min-w-0 bg-zinc-50 dark:bg-zinc-950">{children}</div>
  </ClientSessionLayout>
);

export default ExploreLayout;
